import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/axios';
import {
  FolderIcon,
  DocumentTextIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  EyeIcon,
  LockClosedIcon,
  BookmarkIcon,
  UserIcon,
  TagIcon,
  ChatBubbleLeftIcon,
  BellIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import { Dialog } from '@headlessui/react';

const getFileIcon = (fileType) => {
  if (fileType?.includes('pdf')) return <DocumentTextIcon className="h-8 w-8 text-red-500" />;
  if (fileType?.includes('image')) return <PhotoIcon className="h-8 w-8 text-blue-500" />;
  if (fileType?.includes('word')) return <DocumentTextIcon className="h-8 w-8 text-blue-600" />;
  return <FolderIcon className="h-8 w-8 text-gray-500" />;
};

const getStatusBadge = (status) => {
  const statusConfig = {
    'pending-signature': { 
      label: 'Pending Signature', 
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      icon: <ExclamationTriangleIcon className="h-4 w-4" />
    },
    'signed': { 
      label: 'Signed', 
      color: 'bg-green-100 text-green-800 border-green-200',
      icon: <CheckCircleIcon className="h-4 w-4" />
    },
    'approved': { 
      label: 'Approved', 
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      icon: <CheckCircleIcon className="h-4 w-4" />
    },
    'expired': { 
      label: 'Expired', 
      color: 'bg-red-100 text-red-800 border-red-200',
      icon: <XCircleIcon className="h-4 w-4" />
    },
    'draft': { 
      label: 'Draft', 
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      icon: <ClockIcon className="h-4 w-4" />
    }
  };

  // Default to 'draft' if status is null/undefined
  const config = statusConfig[status] || statusConfig['draft'];
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      {config.icon}
      {config.label}
    </span>
  );
};

const getUploadedByBadge = (uploadedBy) => {
  const userConfig = {
    'admin': { label: 'Admin', color: 'bg-purple-100 text-purple-800' },
    'site-manager': { label: 'Site Manager', color: 'bg-orange-100 text-orange-800' },
    'customer': { label: 'You', color: 'bg-blue-100 text-blue-800' },
    'system': { label: 'System', color: 'bg-gray-100 text-gray-800' }
  };

  // Default to 'customer' if uploadedBy is null/undefined
  const config = userConfig[uploadedBy] || userConfig['customer'];
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <UserIcon className="h-3 w-3" />
      {config.label}
    </span>
  );
};

const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ 
    category: 'All',
    status: 'All',
    uploadedBy: 'All'
  });
  const [downloading, setDownloading] = useState({});
  const [previewModal, setPreviewModal] = useState({ open: false, document: null });
  const [pinnedDocuments, setPinnedDocuments] = useState(new Set());
  const [signing, setSigning] = useState({});
  const [rejecting, setRejecting] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, doc: null, action: null });
  
  // Enhanced categories for construction documents
  const enhancedCategories = [
    'All',
    'Agreements',
    'Receipts', 
    'Blueprints',
    'Permissions',
    'Invoices',
    'Contracts',
    'Permits',
    'Inspections',
    'Warranties',
    'Insurance',
    'General'
  ];

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/documents');
        const documentsData = response.data?.data || response.data || [];
        
        // Use real data from database, no mock data
        const enhancedDocuments = Array.isArray(documentsData) ? documentsData : [];
        
        setDocuments(enhancedDocuments);
        
        // Set pinned documents from real data
        const pinned = new Set(enhancedDocuments.filter(doc => doc.is_pinned).map(doc => doc.id));
        setPinnedDocuments(pinned);
        
      } catch (err) {
        console.error("Failed to fetch documents:", err);
        setError("Could not load documents. Please try again later.");
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, []);

  const filteredDocuments = useMemo(() => {
    let filtered = documents.filter(doc => {
      const matchesCategory = filters.category === 'All' || doc.category === filters.category;
      const matchesStatus = filters.status === 'All' || doc.status === filters.status;
      const matchesUploadedBy = filters.uploadedBy === 'All' || doc.uploaded_by === filters.uploadedBy;
      
      const searchFields = [
        doc.name,
        doc.description,
        doc.version,
        doc.notes,
        doc.category,
        doc.status
      ].join(' ').toLowerCase();
      
      const matchesSearch = searchFields.includes(searchTerm.toLowerCase());
      
      return matchesCategory && matchesStatus && matchesUploadedBy && matchesSearch;
    });

    // Sort: pinned first, then by date
    filtered.sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
    });

    return filtered;
  }, [documents, searchTerm, filters]);

  const handleDownload = async (doc) => {
    if (!doc.file_url) {
      alert('No file URL available for download');
      return;
    }

    try {
      setDownloading(prev => ({ ...prev, [doc.id]: true }));
      // Track download
      try {
        await api.post(`/documents/${doc.id}/track-download`);
      } catch (trackError) {
        console.log('Download tracking failed:', trackError);
      }
      // Method 1: Try using the backend download endpoint
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      // Set correct type and filename for PDF
      const isPdf = (doc.file_type && doc.file_type.includes('pdf')) || (doc.file_url && doc.file_url.endsWith('.pdf'));
      const blob = new Blob([response.data], { type: isPdf ? 'application/pdf' : undefined });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = isPdf ? ((doc.name || 'document') + '.pdf') : (doc.name || 'document');
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      
      // Method 2: Fallback to direct file URL
      try {
        const response = await fetch(doc.file_url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = doc.name || 'document';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
      } catch (fallbackError) {
        console.error('Fallback download failed:', fallbackError);
        
        // Method 3: Final fallback to opening in new tab
        try {
          window.open(doc.file_url, '_blank');
        } catch (finalError) {
          console.error('Final fallback failed:', finalError);
          alert('Download failed. Please try again or contact support.');
        }
      }
    } finally {
      setDownloading(prev => ({ ...prev, [doc.id]: false }));
    }
  };

  const handlePreview = async (doc) => {
    try {
      // Track view
      try {
        await api.post(`/documents/${doc.id}/track-view`);
      } catch (trackError) {
        console.log('View tracking failed:', trackError);
      }
      
      setPreviewModal({ open: true, document: doc });
    } catch (error) {
      console.error('Preview failed:', error);
      alert('Could not preview document. Please try downloading instead.');
    }
  };

  const handlePinToggle = async (docId) => {
    try {
      const isPinned = pinnedDocuments.has(docId);
      const newPinned = new Set(pinnedDocuments);
      
      if (isPinned) {
        newPinned.delete(docId);
      } else {
        newPinned.add(docId);
      }
      
      setPinnedDocuments(newPinned);
      
      // Update in backend
      await api.put(`/documents/${docId}`, { is_pinned: !isPinned });
      
      // Update local state
      setDocuments(prev => prev.map(doc => 
        doc.id === docId ? { ...doc, is_pinned: !isPinned } : doc
      ));
      
    } catch (error) {
      console.error('Pin toggle failed:', error);
      alert('Failed to update pin status. Please try again.');
    }
  };

  const handleSign = async (doc) => {
    setSigning(prev => ({ ...prev, [doc.id]: true }));
    try {
      await api.post(`/documents/${doc.id}/sign`);
      setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'signed', signed_by_customer: true, signed_at: new Date().toISOString() } : d));
    } catch (error) {
      alert('Failed to sign document. Please try again.');
    } finally {
      setSigning(prev => ({ ...prev, [doc.id]: false }));
      setConfirmDialog({ open: false, doc: null, action: null });
    }
  };

  const handleReject = async (doc) => {
    setRejecting(prev => ({ ...prev, [doc.id]: true }));
    try {
      await api.post(`/documents/${doc.id}/reject`);
      setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'rejected' } : d));
    } catch (error) {
      alert('Failed to reject document. Please try again.');
    } finally {
      setRejecting(prev => ({ ...prev, [doc.id]: false }));
      setConfirmDialog({ open: false, doc: null, action: null });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-palamuru-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Documents</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-palamuru-gold text-white px-4 py-2 rounded-lg hover:bg-palamuru-gold-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen font-body">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-display-bold text-gray-900 mb-2">Document Center</h1>
          <p className="text-lg text-gray-600">
            Access all your important project files, agreements, and reports in one place.
          </p>
        </div>

        {/* Enhanced Filters and Search */}
        <div className="bg-white p-6 rounded-2xl shadow-soft border border-gray-100 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents, versions, notes..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-palamuru-blue focus:bg-white outline-none transition"
              />
            </div>
            
            {/* Category Filter */}
            <div className="relative">
              <select
                value={filters.category}
                onChange={e => setFilters({ ...filters, category: e.target.value })}
                className="w-full appearance-none pl-4 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-palamuru-blue focus:bg-white outline-none transition"
              >
                {enhancedCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            </div>
            
            {/* Status Filter */}
            <div className="relative">
              <select
                value={filters.status}
                onChange={e => setFilters({ ...filters, status: e.target.value })}
                className="w-full appearance-none pl-4 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-palamuru-blue focus:bg-white outline-none transition"
              >
                <option value="All">All Status</option>
                <option value="pending-signature">Pending Signature</option>
                <option value="signed">Signed</option>
                <option value="approved">Approved</option>
                <option value="expired">Expired</option>
                <option value="draft">Draft</option>
              </select>
              <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
        
        {/* Documents Table */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="p-4 text-sm font-semibold text-gray-600">Document</th>
                  <th className="p-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="p-4 text-sm font-semibold text-gray-600">Category</th>
                  <th className="p-4 text-sm font-semibold text-gray-600">Uploaded By</th>
                  <th className="p-4 text-sm font-semibold text-gray-600">Version</th>
                  <th className="p-4 text-sm font-semibold text-gray-600">Size</th>
                  <th className="p-4 text-sm font-semibold text-gray-600">Last Modified</th>
                  <th className="p-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.length > 0 ? (
                  filteredDocuments.map(doc => (
                    <tr key={doc.id} className={`border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors ${doc.is_pinned ? 'bg-blue-50' : ''}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            {getFileIcon(doc.file_type)}
                            {doc.is_sensitive && (
                              <LockClosedIcon className="absolute -top-1 -right-1 h-4 w-4 text-red-500 bg-white rounded-full" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-800 truncate">{doc.name}</span>
                              {doc.is_pinned && (
                                <BookmarkIcon className="h-4 w-4 text-blue-500" />
                              )}
                            </div>
                            {doc.notes && (
                              <div className="flex items-center gap-1 mt-1">
                                <ChatBubbleLeftIcon className="h-3 w-3 text-gray-400" />
                                <span className="text-xs text-gray-500 truncate">{doc.notes}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                              <span>👁️ {doc.view_count || 0} views</span>
                              <span>⬇️ {doc.download_count || 0} downloads</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        {getStatusBadge(doc.status)}
                      </td>
                      <td className="p-4 text-gray-600">{doc.category || 'General'}</td>
                      <td className="p-4">
                        {getUploadedByBadge(doc.uploaded_by)}
                      </td>
                      <td className="p-4 text-gray-600 font-mono text-sm">{doc.version || 'v1.0'}</td>
                      <td className="p-4 text-gray-600">{formatFileSize(doc.file_size)}</td>
                      <td className="p-4 text-gray-600">{new Date(doc.updated_at || doc.created_at).toLocaleDateString()}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => handlePreview(doc)}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Preview"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDownload(doc)}
                            disabled={downloading[doc.id]}
                            className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Download"
                          >
                            {downloading[doc.id] ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                            ) : (
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            )}
                          </button>
                          <button 
                            onClick={() => handlePinToggle(doc.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              doc.is_pinned 
                                ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' 
                                : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                            title={doc.is_pinned ? 'Unpin' : 'Pin'}
                          >
                            <BookmarkIcon className="h-4 w-4" />
                          </button>
                          {/* Sign/Reject Buttons for Pending Documents */}
                          {(doc.status === 'pending' || doc.status === 'pending-signature') && (
                            <>
                              <button
                                onClick={() => setConfirmDialog({ open: true, doc, action: 'sign' })}
                                disabled={signing[doc.id]}
                                className="px-3 py-1 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
                              >
                                {signing[doc.id] ? 'Signing...' : 'Sign'}
                              </button>
                              <button
                                onClick={() => setConfirmDialog({ open: true, doc, action: 'reject' })}
                                disabled={rejecting[doc.id]}
                                className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
                              >
                                {rejecting[doc.id] ? 'Rejecting...' : 'Reject'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center text-gray-500 py-16">
                      <h3 className="text-lg font-semibold">No Documents Found</h3>
                      <p>Try adjusting your search or filter criteria.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {previewModal.open && previewModal.document && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{previewModal.document.name}</h2>
                <p className="text-sm text-gray-600">{previewModal.document.description}</p>
              </div>
              <button 
                onClick={() => setPreviewModal({ open: false, document: null })}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 p-6">
              {previewModal.document.file_type?.includes('pdf') ? (
                <iframe
                  src={previewModal.document.file_url}
                  className="w-full h-full border border-gray-200 rounded-lg"
                  title={previewModal.document.name}
                />
              ) : previewModal.document.file_type?.includes('image') ? (
                <img
                  src={previewModal.document.file_url}
                  alt={previewModal.document.name}
                  className="w-full h-full object-contain border border-gray-200 rounded-lg"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center border border-gray-200 rounded-lg bg-gray-50">
                  <div className="text-center">
                    <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Preview not available for this file type</p>
                    <button 
                      onClick={() => handleDownload(previewModal.document)}
                      className="mt-4 bg-palamuru-blue text-white px-4 py-2 rounded-lg hover:bg-palamuru-blue-dark transition-colors"
                    >
                      Download to View
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, doc: null, action: null })} className="fixed z-50 inset-0 flex items-center justify-center">
        <div className="fixed inset-0 bg-black bg-opacity-30" aria-hidden="true" />
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full z-10">
          <Dialog.Title className="text-lg font-semibold mb-2">
            {confirmDialog.action === 'sign' ? 'Sign Document' : 'Reject Document'}
          </Dialog.Title>
          <Dialog.Description className="mb-4 text-gray-600">
            {confirmDialog.action === 'sign'
              ? 'Are you sure you want to sign/approve this document? This action cannot be undone.'
              : 'Are you sure you want to reject this document?'}
          </Dialog.Description>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setConfirmDialog({ open: false, doc: null, action: null })}
              className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Cancel
            </button>
            {confirmDialog.action === 'sign' ? (
              <button
                onClick={() => handleSign(confirmDialog.doc)}
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
                disabled={signing[confirmDialog.doc?.id]}
              >
                {signing[confirmDialog.doc?.id] ? 'Signing...' : 'Sign'}
              </button>
            ) : (
              <button
                onClick={() => handleReject(confirmDialog.doc)}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
                disabled={rejecting[confirmDialog.doc?.id]}
              >
                {rejecting[confirmDialog.doc?.id] ? 'Rejecting...' : 'Reject'}
              </button>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default DocumentsPage;